import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import axios from '../../api/axios';

// Quantity Change Modal Component
const QuantityChangeModal = ({ item, onClose, onProceed }) => {
  const [quantity, setQuantity] = useState(item.quantity);
  const [reason, setReason] = useState('');

  const handleProceed = async () => {
    try {
      const response = await axios.put('retail/update-quantity', {
        order_item_id: item.order_item_id,
        new_quantity: quantity,
        order_id: item.order_id
      });
      onClose();
      onProceed(quantity, reason, response.data);



    } catch (error) {
      console.error('Error updating quantity:', error);
      // Optionally show error to user
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white p-6 rounded-lg w-96">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-lg">Change Quantity</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">New Quantity</label>
            <input
              type="number"
              className="w-full border rounded p-2"
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              min="1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Reason</label>
            <input
              type="text"
              className="w-full border rounded p-2"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for change"
            />
          </div>

          <div className="flex gap-2 justify-end mt-6">
            <button
              className="px-4 py-2 bg-gray-200 rounded"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-blue-500 text-white rounded"
              onClick={handleProceed}
            >
              Proceed
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Discount Modal Component
const DiscountModal = ({ item, onClose, onProceed }) => {
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState('');

  const handleProceed = async () => {
    try {
      const response = await axios.put('retail/update-discount', {
        order_item_id: item.order_item_id,
        discount_type: discountType,
        discount_value: Number(discountValue),
        order_id: item.order_id
      });

      onProceed(discountType, discountValue, response.data);
    } catch (error) {
      console.error('Error applying discount:', error);
      // Optionally show error to user
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white p-6 rounded-lg w-96">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-lg">Add Item Discount</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Discount Type</label>
            <select
              className="w-full border rounded p-2"
              value={discountType}
              onChange={(e) => setDiscountType(e.target.value)}
            >
              <option value="percentage">Percentage (%)</option>
              <option value="fixed">Fixed Amount (₹)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Discount Value
              <span className="text-gray-500 text-sm ml-2">
                (Applies to this item only)
              </span>
            </label>
            <input
              type="number"
              className="w-full border rounded p-2"
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              placeholder={`Enter amount in ${discountType === 'percentage' ? '%' : '₹'}`}
            />
          </div>

          {/* Preview section */}
          {discountValue && (
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-sm font-medium">Preview:</p>
              <div className="text-sm">
                <div>Item Total: ₹{(item.unit_price * item.quantity).toFixed(2)}</div>
                <div>Discount: {
                  discountType === 'percentage'
                    ? `${discountValue}% = ₹${((item.unit_price * item.quantity * Number(discountValue)) / 100).toFixed(2)}`
                    : `₹${Number(discountValue).toFixed(2)}`
                }</div>
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-end mt-6">
            <button
              className="px-4 py-2 bg-gray-200 rounded"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-blue-500 text-white rounded"
              onClick={handleProceed}
            >
              Apply Discount
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Item Actions Menu Component
const ItemActionsMenu = ({ item, onClose, onRemove, onQuantityChange, onDiscount }) => {
  const ref = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  return (
    <div ref={ref} className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10">
      <button
        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
        onClick={onRemove}
      >
        Remove Item
      </button>
      <button
        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
        onClick={onQuantityChange}
      >
        Change Quantity
      </button>
      <button
        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
        onClick={onDiscount}
      >
        Add Discount
      </button>
    </div>
  );
};

// Enhanced Cart Item Row Component
const CartItemRow = ({
  item,
  saved,
  onRemove,
  onQuantityChange,
  onDiscountChange
}) => {
  console.log("hjhjhj", item)
  const [showActions, setShowActions] = useState(false);
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(item);

  const actionsMenuRef = useRef(null);
  const quantityModalRef = useRef(null);
  const discountModalRef = useRef(null);

  const handleOutsideClick = (event) => {
    // If the click is outside the actions menu or modal, close the actions menu
    if (
      actionsMenuRef.current &&
      !actionsMenuRef.current.contains(event.target) &&
      (!quantityModalRef.current || !quantityModalRef.current.contains(event.target)) &&
      (!discountModalRef.current || !discountModalRef.current.contains(event.target))
    ) {
      setShowActions(false);
    }
  };

  useEffect(() => {
    if (showActions) {
      document.addEventListener('mousedown', handleOutsideClick);
    } else {
      document.removeEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showActions]);

  const handleRemoveItem = async () => {
    try {
      const response = await axios.delete('retail/delete-Order-Item', {
        data: { order_item_id: currentItem.order_item_id, order_id: currentItem.order_id }
      });

      onRemove(currentItem.order_item_id);
      setShowActions(false);
    } catch (error) {
      console.error('Error removing item:', error);
      // Optionally show error to user
    }
  };

  const handleQuantityProceed = (newQuantity, reason, apiResponse) => {
    // Update local state with API response
    setCurrentItem(prevItem => ({
      ...prevItem,
      quantity: newQuantity,
      total_price: apiResponse.total_price
    }));
    // Call parent component's quantity change handler
    onQuantityChange(currentItem.order_item_id, newQuantity, reason);
    setShowQuantityModal(false);
  };

  const handleDiscountProceed = (type, value, apiResponse) => {
    const updatedItem = {
      ...currentItem,
      discount_type: type,
      discount: value,
      discounted_price: apiResponse.discounted_price,
      total_discount: apiResponse.total_discount
    };
    setCurrentItem(updatedItem);
    onDiscountChange(currentItem.order_item_id, type, value);
    setShowDiscountModal(false);
  };

  const getDisplayDiscount = () => {
    if (!currentItem.discount || currentItem.discount <= 0) {
      return "0";
    }
    return currentItem.discount_type === 'percentage'
      ? `${currentItem.discount}%`
      : `₹${currentItem.discount}`;
  };

  const getItemTotal = () => {
    const subtotal = currentItem.unit_price * currentItem.quantity;
    const discountAmount = currentItem.total_discount || 0;
    const afterDiscount = subtotal - discountAmount;
    return {
      subtotal,
      afterDiscount,
      cgst: currentItem.cgst || 0,
      sgst: currentItem.sgst || 0
    };
  };

  const price = currentItem.unit_price > 0 ? currentItem.unit_price : currentItem.item_price;
  const itemQuantity = item.quantity > 0 ? item.quantity : currentItem.quantity;
  const discountcondition = currentItem.discount;
  return (
    <div
      className="row py-2 border-bottom fw-semibold small"
      onClick={(e) => {
        // Prevent the actions menu from toggling if the click is inside the modal
        if (!showQuantityModal && !showDiscountModal) {
          saved && setShowActions(true);
        }
      }}
    >
      <div className="col-3">{currentItem.item_id}</div>
      <div className="col-3">{currentItem.item_name}</div>
      <div className="col-3">₹{price}</div>
      <div className="col-1">{itemQuantity}</div>
      <div className="col-2">{getDisplayDiscount()}</div>

      {showActions && (
        <div ref={actionsMenuRef}>
          <ItemActionsMenu
            item={currentItem}
            onClose={() => setShowActions(false)}
            onRemove={handleRemoveItem}
            onQuantityChange={() => {
              setShowActions(false);
              setShowQuantityModal(true);
            }}
            onDiscount={() => {
              setShowActions(false);
              setShowDiscountModal(true);
            }}
          />
        </div>
      )}

      {showQuantityModal && (
        <div ref={quantityModalRef}>
          <QuantityChangeModal
            item={currentItem}
            onClose={() => setShowQuantityModal(false)}
            onProceed={handleQuantityProceed}
          />
        </div>
      )}

      {showDiscountModal && (
        <div ref={discountModalRef}>
          <DiscountModal
            item={currentItem}
            onClose={() => setShowDiscountModal(false)}
            onProceed={handleDiscountProceed}
          />
        </div>
      )}
    </div>
  );
};

export default CartItemRow;